import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Autocomplete,
  Box,
  CardActionArea,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { useTestData } from "context/TestDataContext";
import { useTranslation } from "i18n/LanguageContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { calcAge } from "utils/dateUtils";
import MenuBar from "../components/MenuBar";
import ProfileDialog from "../dialogs/ProfileDialog";
import { SecondaryColoredBorderCard } from "../style/MUIStylePresets";
import { formatGeria12Value } from "utils/geria12";

export default function HomeDash() {
  const navigate = useNavigate();
  const { testData, updateTest, patientInfo, updatePatientInfo, resetPatientInfo, resetTestData, setSelectedProfile, profiles } = useTestData();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const age = calcAge(patientInfo.dateNaissance, patientInfo.dateExamen);

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    navigate("/newscoresheet");
  };

  const applyGeria12Result = (result) => {
    let count = 0;
    Object.entries(testData).forEach(([testName, t]) => {
      if (!t?.geria12Cells) return;
      const updatedParams = (t.params || []).map((p) => {
        const cellRef = t.geria12Cells[p.name];
        if (!cellRef) return p;
        const v = result.cells[cellRef];
        if (v == null) return p;
        return { ...p, value: formatGeria12Value(v, p.name) };
      });
      updateTest(testName, { params: updatedParams, geria12File: result.fileName });
      count++;
    });
    setSnackbar({
      severity: "success",
      message: t("home.geria.success", { file: result.fileName, count }),
    });
  };

  const handlePageDragOver = (e) => {
    if (e.dataTransfer?.types?.includes("Files")) {
      e.preventDefault();
      setDragActive(true);
    }
  };

  const handlePageDragLeave = (e) => {
    if (!e.relatedTarget || e.relatedTarget.nodeName === "HTML") {
      setDragActive(false);
    }
  };

  const handlePageDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      setSnackbar({ severity: "error", message: t("home.geria.dropTypeError") });
      return;
    }
    try {
      const result = await window.electronAPI.parseGeria12XLSFromPath(file);
      if (result.canceled) return;
      if (!result.success) {
        setSnackbar({ severity: "error", message: result.error || t("home.geria.readError") });
        return;
      }
      applyGeria12Result(result);
    } catch (err) {
      console.error("GERIA-12 XLS upload error:", err);
      setSnackbar({ severity: "error", message: err.message });
    }
  };

  return (
    <Box
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
      sx={{ minHeight: "100vh", position: "relative" }}
    >
      {dragActive && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(32, 81, 164, 0.08)",
            border: "3px dashed",
            borderColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1300,
          }}
        >
          <Typography variant="h4" color="primary">
            {t("home.dropGeria")}
          </Typography>
        </Box>
      )}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar?.severity || "info"}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
      <Box mx={"5%"} mt={15}>
      <MenuBar />
      <Box mt={12}>
        <Box>
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" color="primary" gutterBottom>
                {t("home.patientInfo")}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { resetPatientInfo(); resetTestData(); }}
              >
                {t("home.newPatient")}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("home.nom")}
                  value={patientInfo.nom}
                  onChange={(e) =>
                    updatePatientInfo("nom", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("home.prenom")}
                  value={patientInfo.prenom}
                  onChange={(e) =>
                    updatePatientInfo("prenom", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={`${t("home.dateNaissance")}${age !== null ? ` (${t("home.age")}: ${age} ${t("home.years")})` : ""}`}
                  type="date"
                  value={patientInfo.dateNaissance}
                  onChange={(e) =>
                    updatePatientInfo("dateNaissance", e.target.value)
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: "9999-12-31" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("home.dateExamen")}
                  type="date"
                  value={patientInfo.dateExamen}
                  onChange={(e) =>
                    updatePatientInfo("dateExamen", e.target.value)
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: "9999-12-31" }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t("home.dateExamenComparatif1")}
                  type="date"
                  value={patientInfo.dateExamenComparatif1}
                  onChange={(e) =>
                    updatePatientInfo("dateExamenComparatif1", e.target.value)
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: "9999-12-31" }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t("home.dateExamenComparatif2")}
                  type="date"
                  value={patientInfo.dateExamenComparatif2}
                  onChange={(e) =>
                    updatePatientInfo("dateExamenComparatif2", e.target.value)
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: "9999-12-31" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={t("home.lateralite")}
                  value={patientInfo.lateralite}
                  onChange={(e) =>
                    updatePatientInfo("lateralite", e.target.value)
                  }
                  fullWidth
                >
                  <MenuItem value="D">{t("home.lateralite.D")}</MenuItem>
                  <MenuItem value="G">{t("home.lateralite.G")}</MenuItem>
                  <MenuItem value="A">{t("home.lateralite.A")}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={t("home.genre")}
                  value={patientInfo.genre}
                  onChange={(e) =>
                    updatePatientInfo("genre", e.target.value)
                  }
                  fullWidth
                >
                  <MenuItem value="H">{t("home.genre.H")}</MenuItem>
                  <MenuItem value="F">{t("home.genre.F")}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={t("home.niveau")}
                  value={patientInfo.niveau}
                  onChange={(e) =>
                    updatePatientInfo("niveau", e.target.value)
                  }
                  fullWidth
                >
                  <MenuItem value="I">I</MenuItem>
                  <MenuItem value="II">II</MenuItem>
                  <MenuItem value="III">III</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={["FR", "DE"]}
                  value={patientInfo.langueMaternelle}
                  onInputChange={(e, newValue) =>
                    updatePatientInfo("langueMaternelle", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t("home.langueMaternelle")} />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("home.anneesEtudes")}
                  type="number"
                  value={patientInfo.anneesEtudes}
                  onChange={(e) =>
                    updatePatientInfo("anneesEtudes", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
          <Box mt={5}></Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" color="primary" gutterBottom>
              {t("home.profile")}
            </Typography>
            <IconButton
              color="primary"
              onClick={() => { setEditingIndex(null); setDialogOpen(true); }}
            >
              <AddIcon />
            </IconButton>
          </Box>
          <Divider />
          {profiles.map((profile, index) => (
            <Box key={index} mt={2}>
              <SecondaryColoredBorderCard borderWidth={2}>
                <Box display="flex" alignItems="center">
                  <CardActionArea onClick={() => handleProfileSelect(profile)} sx={{ flexGrow: 1 }}>
                    <Stack m={2}>
                      <Typography variant="h6" color={"primary"}>
                        {profile.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t("home.tests")}: {profile.tests.join(", ")}
                      </Typography>
                    </Stack>
                  </CardActionArea>
                  <IconButton
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => { setEditingIndex(index); setDialogOpen(true); }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </SecondaryColoredBorderCard>
            </Box>
          ))}
          <ProfileDialog
            open={dialogOpen}
            handleClose={() => setDialogOpen(false)}
            editingIndex={editingIndex}
          />
        </Box>
      </Box>
      </Box>
    </Box>
  );
}
